import { useState } from "react";
import { Building2, Save } from "lucide-react";
import type { OrganizationIdentity } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/notify";
import { useUpdateOrganization } from "./hooks";

interface Props {
  organization: OrganizationIdentity;
  canEdit: boolean;
}

// Nome e FUSO da fábrica.
// O fuso já existia na coluna; o que não existia era qualquer forma de editá-lo que não fosse
// `psql`. Ele decide em que hora civil todo turno declarado é resolvido — "06:00–14:00" só vira
// instante depois de passar por ele —, então uma fábrica que precisasse corrigi-lo dependia de um
// DBA.
// A lista é curta e explícita em vez de `Intl.supportedValuesOf("timeZone")` (~400 entradas): a
// plataforma opera no Brasil, e rolar quatrocentos fusos para achar Fortaleza é pior do que não ter
// a opção. Fábrica fora desta lista é caso a tratar quando existir, não a antecipar num combo.
const TIMEZONES = [
  { value: "America/Fortaleza", label: "Fortaleza (UTC−3)" },
  { value: "America/Sao_Paulo", label: "São Paulo / Brasília (UTC−3)" },
  { value: "America/Bahia", label: "Salvador (UTC−3)" },
  { value: "America/Recife", label: "Recife (UTC−3)" },
  { value: "America/Belem", label: "Belém (UTC−3)" },
  { value: "America/Manaus", label: "Manaus (UTC−4)" },
  { value: "America/Cuiaba", label: "Cuiabá (UTC−4)" },
  { value: "America/Porto_Velho", label: "Porto Velho (UTC−4)" },
  { value: "America/Boa_Vista", label: "Boa Vista (UTC−4)" },
  { value: "America/Rio_Branco", label: "Rio Branco (UTC−5)" },
  { value: "America/Noronha", label: "Fernando de Noronha (UTC−2)" },
];

export function OrganizationIdentitySection({ organization, canEdit }: Props) {
  const [name, setName] = useState(organization.name);
  const [timezone, setTimezone] = useState(organization.timezone);
  const update = useUpdateOrganization();

  const dirty = name.trim() !== organization.name || timezone !== organization.timezone;
  const known = TIMEZONES.some((t) => t.value === timezone);

  async function save() {
    await update.mutateAsync({ name: name.trim(), timezone });
    toast.success("Dados da fábrica salvos.");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-text-tertiary" aria-hidden />
          Identificação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="org-name">Nome da fábrica</Label>
            <Input
              id="org-name"
              value={name}
              disabled={!canEdit || update.isPending}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-timezone">Fuso horário</Label>
            <Select
              id="org-timezone"
              value={timezone}
              disabled={!canEdit || update.isPending}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {/* Fuso vindo do banco fora da lista curta: exibido em vez de silenciosamente
                  trocado pelo primeiro item, o que reescreveria a configuração ao salvar. */}
              {known ? null : <option value={timezone}>{timezone}</option>}
              {TIMEZONES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <p className="text-[12px] text-text-secondary">
          Os turnos são declarados em hora local. É o fuso que define a que horas eles acontecem de
          fato — mudá-lo desloca o expediente de todas as linhas, inclusive em períodos já
          encerrados.
        </p>

        {/* "Salvar identificação", não "Salvar": a tela tem dois botões de salvar (este e o do
            expediente), e dois controles chamados "Salvar" na mesma página são ambíguos —
            principalmente para quem navega por lista de botões. */}
        {canEdit ? (
          <Button type="button" disabled={!dirty || update.isPending} onClick={() => void save()}>
            <Save className="mr-1.5 h-4 w-4" aria-hidden /> Salvar identificação
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
